import { useEffect } from "react";
import styled from "styled-components";
import { Tooltip } from "react-tooltip";

import { numberWithCommas } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";

import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import userCircleIcon from "assets/img/icons/user-single-icon.svg";
import userCircleWhiteIcon from "assets/img/icons/user-single-icon-white.svg";
import percentWhiteIcon from "assets/img/icons/percent-icon-white.svg";

const MailingListList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 6px;
    margin-bottom: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const MailingListBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;
  max-width: 750px;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  transition: 0.2s;

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const BoxTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const ListName = styled.div`
  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
  overflow-wrap: anywhere;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 0px;
  }
`;

const BoxColumns = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    gap: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const BoxColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxColumnTitle = styled.div`
  font-weight: 500;
`;

const StatsBox = styled.div`
  padding: ${css.BOX_PADDING_TERTIARY};
  max-width: 260px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;

  display: grid;
  grid-template-columns: 32px 73px 73px 1fr;
  grid-template-rows: 25px 1fr 1fr 1fr;
`;

const StatsLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIconCell = styled.div`
  display: flex;
  align-items: center;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;

  display: flex;
  align-items: center;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const MailingListPageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MailingListPage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MailingListPageName = styled.div`
  line-height: 1;
`;

const MailingListPageStatus = styled.div``;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px;

  font-size: 13px;
  color: #fff;
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) => {
    switch (props.status) {
      case "active":
        return css.BRAND_GREEN;
    }
  }};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const LabelIconWithTip = ({ icon, tip }) => {
  return (
    <LabelIcon>
      <img
        src={icon}
        style={{ width: "100%" }}
        data-tooltip-id={`${icon}-tip`}
        data-tooltip-content={tip}
        data-tooltip-place="top"
        data-tooltip-delay-show={500}
      />
      <Tooltip id={`${icon}-tip`} className="tooltip" />
    </LabelIcon>
  );
};

export default ({ data, limit, page, paginate, refetch }) => {
  const numOfResults = data.mailingListListData.numOfResults;

  const changeBadge = (change) => {
    if (typeof change !== "number") return <></>;

    if (change > 100) return <ChangeBadge green={true}>&gt;100%</ChangeBadge>;

    const roundCoef = change >= 10 || change <= -10 ? 1 : 10;
    let changeRounded = Math.round(change * roundCoef) / roundCoef;

    if (changeRounded >= 0)
      return <ChangeBadge green={true}>+{changeRounded}%</ChangeBadge>;
    else return <ChangeBadge green={false}>{changeRounded}%</ChangeBadge>;
  };

  const reduceValue = (value) => {
    if (value < 10000) return numberWithCommas(value);
    return Math.round(value / 1000, 0) + "k";
  };

  const list = data.mailingListListData.lists.map((list) => {
    const { id, name, pages } = list;

    let statusText;

    const pageList = pages.map((page) => {
      switch (page.status) {
        case "active":
          statusText = "Active";
          break;
      }

      return (
        <MailingListPage>
          <MailingListPageName>{page.name}</MailingListPageName>
          <MailingListPageStatus>
            <StatusBadge status={page.status}>{statusText}</StatusBadge>
          </MailingListPageStatus>
        </MailingListPage>
      );
    });

    return (
      <MailingListBox key={id}>
        <BoxTop>
          <ListName>{name}</ListName>
        </BoxTop>
        <BoxColumns>
          <BoxColumn>
            <BoxColumnTitle>List Stats</BoxColumnTitle>
            <StatsBox>
              <StatsLabel></StatsLabel>
              <StatsLabel>24 Hours</StatsLabel>
              <StatsLabel>7 Days</StatsLabel>
              <StatsLabel>Total</StatsLabel>
              <LabelIconCell>
                <LabelIconWithTip icon={userCircleIcon} tip="Leads" />
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(9)}
                {changeBadge(12)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(58)}
                {changeBadge(20)}
              </ValueCell>
              <ValueCell>{reduceValue(450)}</ValueCell>
              <LabelIconCell>
                <LabelIconWithTip
                  icon={userCircleWhiteIcon}
                  tip="Verified Leads"
                />
              </LabelIconCell>
              <ValueCell>
                {numberWithCommas(2)}
                {changeBadge(-5)}
              </ValueCell>
              <ValueCell>
                {numberWithCommas(12)}
                {changeBadge(-7)}
              </ValueCell>
              <ValueCell>{reduceValue(25)}</ValueCell>
              <LabelIconCell>
                <LabelIconCell>
                  <LabelIconWithTip
                    icon={percentWhiteIcon}
                    tip="Verified leads percentage"
                  />
                </LabelIconCell>
              </LabelIconCell>
              <ValueCell>{12}%</ValueCell>
              <ValueCell>{14}%</ValueCell>
              <ValueCell>{13}%</ValueCell>
            </StatsBox>
          </BoxColumn>
          <BoxColumn>
            <BoxColumnTitle>Connected Pages</BoxColumnTitle>
            <MailingListPageList>{pageList}</MailingListPageList>
          </BoxColumn>
        </BoxColumns>
      </MailingListBox>
    );
  });

  // prepare pagination
  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  return (
    <MailingListList>
      {list}
      {showPagination && (
        <Pagination>
          {page > 1 && (
            <ButtonRoundIcon
              icon={leftIcon}
              hoverIcon={leftWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("previous")}
            />
          )}
          {page < maxPage && (
            <ButtonRoundIcon
              icon={rightIcon}
              hoverIcon={rightWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              iconSize="16px"
              buttonSize="32px"
              onClick={() => paginate("next")}
            />
          )}
        </Pagination>
      )}
    </MailingListList>
  );
};
